import {
  createTheme,
  IComboBoxStyles,
  IDetailsColumnStyles,
  IIconProps,
  IImageProps,
  ILayerProps,
  IRawStyle,
  ISearchBoxStyles,
  IStackItemStyles,
  IStackStyles,
  IStackTokens,
  ITextFieldStyles,
  ITextStyles,
  ITheme,
  Theme,
} from '@fluentui/react';
import { IAppContext } from 'App/AppContext';
import { IPImageFit } from 'baseComponents/Image/IPImage';
import Config from 'services/Config/configService';
import { IPSharedColors } from 'themes/runtime/fluentColors';
import { lightTheme } from 'themes/runtime/globalThemes-v8';

//
// App global
//
export const globalFontBoldWeight: string = '600';
export const globalLayerRightSideBarProps: ILayerProps = { hostId: 'layerHostRightSideBar' };
export const globalLayerHostSceneId: string = 'layerHostScene';
export const globalLayerSceneProps: ILayerProps = { hostId: globalLayerHostSceneId };
export const globalBoxShadow: string = '0 1px 2px 0 #c9c9c9';
export const globalBoxBorder: string = '1px solid #c9c9c9';
export const globalBoxBorderDarkMode: string = '1px solid #585858';
export const globalBoxBorderRadius: string = '8px';
export const globalCalloutShadow: string =
  'rgba(0, 0, 0, 0.18) 0px 10.4px 20.4px 0px, rgba(0, 0, 0, 0.18) 0px 10.2px 20.6px 0px';
export const globalPillColor = '#92b3f1';
export const globalPillSelectedColor = '#5a7ec0';

//
// Scene global
//
export const globalPaddingScene: number = 20;
export const globalSceneBarItemHeight: number = 44;

export const globalSceneBarItemStyles: IStackItemStyles = {
  root: {
    height: globalSceneBarItemHeight,
    justifyContent: 'center',
  },
};

//
// Stacks styles
//
export const globalStackStylesHeight100: IStackStyles = {
  root: {
    height: '100%',
  },
};

export const globalStackStylesPaddingScene: IStackStyles = {
  root: {
    paddingLeft: globalPaddingScene,
    paddingRight: globalPaddingScene,
  },
};

export const globalStackStylesHeight100PaddingScene: IStackStyles = {
  root: {
    height: '100%',
    paddingLeft: globalPaddingScene,
    paddingRight: globalPaddingScene,
  },
};

export const globalStackStylesHeight100PaddingSmall: IStackStyles = {
  root: {
    height: '100%',
    padding: 10,
  },
};

export const globalStackStylesHeight100PaddingMedium: IStackStyles = {
  root: {
    height: '100%',
    padding: 20,
  },
};

export const globalStackStylesNoHeightPaddingSmall: IStackStyles = {
  root: {
    padding: 10,
  },
};

export const globalStackStylesBottomSmall: IStackStyles = {
  root: {
    paddingBottom: 10,
  },
};

//an inline icon button is 32 high: reserving that height keeps a row from growing when the button is
//only rendered in edit mode
export const globalStackStylesIconButtonRow: IStackStyles = {
  root: {
    minHeight: 32,
  },
};

export const globalStackStylesResourcePanel: IStackStyles = {
  root: {
    paddingTop: 20,
    paddingBottom: 10,
    paddingLeft: 20,
    paddingRight: 10,
  },
};

export const globalStackStylesPanel: IStackStyles = {
  root: {
    paddingTop: 30,
    paddingBottom: 10,
  },
};

export const globalStackStylesPanelFooter: IStackStyles = {
  root: {
    padding: 30,
  },
};

//
// Card styles
//
export const globalStackStylesCard: IStackStyles = {
  root: {
    height: '100%',
    paddingLeft: 15,
    paddingRight: 10,
    paddingTop: 10,
    paddingBottom: 10,
  },
};

export const globalStackItemStylesCard: IStackItemStyles = {
  root: {
    padding: 0,
    margin: 0,
  },
};

export const globalStackTokensGapCard: IStackTokens = {
  childrenGap: 0,
};

export const globalTextStylesCard: ITextStyles = {
  root: {
    paddingTop: 0,
    paddingBottom: 0,
  },
};

export const globalTextStylesCardRed: ITextStyles = {
  root: {
    paddingTop: 0,
    paddingBottom: 0,
    color: 'red',
  },
};

export const globalTextStylesCardBold: ITextStyles = {
  root: {
    paddingTop: 0,
    paddingBottom: 0,
    fontWeight: globalFontBoldWeight,
  },
};

export const globalTextStylesCardItalic: ITextStyles = {
  root: {
    paddingTop: 0,
    paddingBottom: 0,
    fontStyle: 'italic',
  },
};

export const globalTextStylesNoWrap: ITextStyles = {
  root: {
    whiteSpace: 'nowrap',
  },
};

//Clamps a text to a number of lines with an ellipsis. Use it as `styles={{ root: getGlobalTextStylesLineClamp(2) }}`.
export const getGlobalTextStylesLineClamp = (lines: number): IRawStyle => {
  return {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    // A string, not a number: merge-styles appends `px` to a unitless number, and `-webkit-line-clamp: 2px`
    // is invalid, so the whole declaration would be dropped and the text would wrap without limit.
    WebkitLineClamp: lines.toString(),
    overflow: 'hidden',
    wordBreak: 'break-word',
  };
};

export const globalTextStylesOverlineRoot: IRawStyle = {
  textTransform: 'uppercase',
  letterSpacing: '.6px',
};

//
// Stacks item styles
//
export const globalStackItemStylesPaddingScene: IStackItemStyles = {
  root: {
    paddingLeft: globalPaddingScene,
    paddingRight: globalPaddingScene,
  },
};

export const globalStackItemStylesScroll: IStackItemStyles = {
  root: {
    position: 'relative',
  },
};

export const globalStackItemStylesPaddingSceneScroll: IStackItemStyles = {
  root: {
    paddingLeft: globalPaddingScene,
    paddingRight: globalPaddingScene,
    position: 'relative',
  },
};

export const getGlobalStackItemStylesPaddingSceneScrollMinHeight = (minHeight: number): IStackItemStyles => {
  return {
    root: {
      minHeight: minHeight,
      paddingLeft: globalPaddingScene,
      paddingRight: globalPaddingScene,
      position: 'relative',
    },
  };
};

export const globalStackItemStylesPaddingSmall: IStackItemStyles = {
  root: {
    paddingLeft: 10,
    paddingRight: 10,
  },
};

//
// Stack tokens
//
export const globalStackTokensGapExtraSmall: IStackTokens = {
  childrenGap: 5,
};

export const globalStackTokensGapSmall: IStackTokens = {
  childrenGap: 10,
};

export const globalStackTokensGapMedium: IStackTokens = {
  childrenGap: 20,
};

export const globalStackTokensGapLarge: IStackTokens = {
  childrenGap: 30,
};

export const globalStackTokensGapLargePlus5: IStackTokens = {
  childrenGap: 35,
};

export const globalStackTokensGapXLarge: IStackTokens = {
  childrenGap: 50,
};

export const globalStackTokensPropertySet: IStackTokens = {
  childrenGap: '5 35',
};

// Wrapping rows of dashboard tiles: medium gap between wrapped rows, large gap between columns
export const globalStackTokensGapTileRow: IStackTokens = {
  childrenGap: '20 30',
};

export const globalStackItemStylesPaddingSceneWithFixedHeight = (fixedHeight: number): IStackItemStyles => {
  return {
    root: {
      paddingLeft: globalPaddingScene,
      paddingRight: globalPaddingScene,
      height: fixedHeight,
    },
  };
};

//
// Searchbox styles
//
export const globalSearchBoxStyles: Partial<ISearchBoxStyles> = { root: { width: 200 } };

/** An action button as a quiet link for something that is not required: no padding, so it lines up with text. */
export const globalActionLinkStyles = { root: { paddingLeft: 0, height: 32 }, icon: { marginLeft: 0 } };

//
// Combobox styles
//
export const globalComboContainerHeight: Partial<IComboBoxStyles> = {
  optionsContainer: {
    maxHeight: 350,
  },
};

//
// Text styles
//
export const globalTextStylesBold: ITextStyles = {
  root: {
    fontWeight: globalFontBoldWeight,
  },
};

export const globalTextStylesBoldBlack: ITextStyles = {
  root: {
    fontWeight: globalFontBoldWeight,
    color: 'black',
  },
};

export const globalTextStylesBoldWhite: ITextStyles = {
  root: {
    fontWeight: globalFontBoldWeight,
    color: 'white',
  },
};

export const globalTextStylesBoldBlue = (theme: Theme, width?: number): ITextStyles => {
  return {
    root: {
      fontWeight: globalFontBoldWeight,
      color: theme.palette.themePrimary,
      maxWidth: width,
    },
  };
};

export const globalTextStylesPaddingScene: ITextStyles = {
  root: {
    paddingLeft: globalPaddingScene,
    paddingRight: globalPaddingScene,
  },
};

export const globalTextStylesPaddingSceneBold: ITextStyles = {
  root: {
    paddingLeft: globalPaddingScene,
    paddingRight: globalPaddingScene,
    fontWeight: globalFontBoldWeight,
  },
};

export const globalTextStylesPaddingSmallBold: ITextStyles = {
  root: {
    paddingLeft: 10,
    paddingRight: 10,
    fontWeight: globalFontBoldWeight,
  },
};

export const globalTextStylesBoldPaddingSmall: ITextStyles = {
  root: {
    paddingLeft: 10,
    paddingRight: 10,
    fontWeight: globalFontBoldWeight,
  },
};

export const globalTextStylesPaddingSmall: ITextStyles = {
  root: {
    paddingLeft: 10,
    paddingRight: 10,
  },
};

export const globalTextStylesPaddingLabelPanel: ITextStyles = {
  root: {
    paddingBottom: 6,
    fontWeight: globalFontBoldWeight,
  },
};

export const globalTextStylesMarginTopLabelPanel: ITextStyles = {
  root: {
    marginTop: 10,
  },
};

// ---------------------------------------------------------------------------
// Theme-aware static text styles
//
// The four global text-style exports below (Error, ValidationError, Disabled,
// Category) used to hardcode hex colours which were unreadable in dark mode.
// They now reference CSS variables on :root that are toggled by
// applyThemeCssVars based on appContext.useDarkMode.
//
// Why CSS variables: the styles are static exports used in 100+ call sites,
// so making them theme-aware at the call-site (passing useDarkMode through
// useTheme/useContext) would mean editing every consumer. CSS variables fix
// the colour at the definition layer -- callers stay unchanged. This is also
// the direction Fluent UI v9 went architecturally for theming.
//
// Adding a new theme-aware variable: add APP_X_LIGHT/DARK constants below,
// setProperty('--app-x', ...) in applyThemeCssVars, reference via var(...) in
// the style export.
// ---------------------------------------------------------------------------

const APP_TEXT_ERROR_LIGHT = '#BC1E1E';
const APP_TEXT_ERROR_DARK = '#F1707B'; // matches Fluent UI semantic errorText for dark
const APP_TEXT_MUTED_LIGHT = '#909090'; // grey used for disabled state and category labels
const APP_TEXT_MUTED_DARK = '#B5B5B5'; // lighter grey, AAA contrast on Fluent dark surfaces

// Project rail/shell neutrals: hairline, rail spine, row hover/viewed band, done check, rail tint.
// Light values come from `lightTheme`. Dark values are hardcoded because the dark palette's
// corresponding slots stay too close to the dark canvas to give these roles readable contrast.
const lightPalette = createTheme(lightTheme).palette;

const APP_DIVIDER_LIGHT = lightPalette.neutralLight;
const APP_DIVIDER_DARK = '#3b3a39'; // hairline: rail/content border, header bottom border, row separators
const APP_LINE_STRONG_LIGHT = lightPalette.neutralTertiaryAlt;
const APP_LINE_STRONG_DARK = '#4a4846'; // the rail spine (dark)
const APP_ROW_HOVER_LIGHT = lightPalette.neutralLighter;
const APP_ROW_HOVER_DARK = '#3d3a37'; // viewed band / row hover (dark)
const APP_SUCCESS_LIGHT = IPSharedColors.green20; // the same green TaskList.tsx uses for a done check
const APP_SUCCESS_DARK = '#6ccb5f'; // done check / confirmed states (dark)

// Project rail tint (v8 bodyStandoutBackground role): one step off the page, both themes.
const APP_SURFACE_STANDOUT_LIGHT = lightPalette.neutralLighterAlt;
const APP_SURFACE_STANDOUT_DARK = '#222120'; // the same one step off the page, in dark mode

// Overlay card surface (OverlayLoader): the busy card floating above the page. No v8 semantic
// slot covers it -- bodyBackground is the dark palette's inverted white (#2a2a2a), which sits
// just above the dark canvas instead of reading as a distinct surface.
const APP_SURFACE_OVERLAY_LIGHT = lightPalette.white;
const APP_SURFACE_OVERLAY_DARK = '#000000'; // black, so the card separates from the dark canvas

export const globalTextStylesError: ITextStyles = {
  root: {
    color: `var(--app-text-error, ${APP_TEXT_ERROR_LIGHT})`,
    textDecorationColor: `var(--app-text-error, ${APP_TEXT_ERROR_LIGHT})`,
  },
};

export const globalTextStylesValidationError: ITextStyles = {
  root: {
    color: `var(--app-text-error, ${APP_TEXT_ERROR_LIGHT})`,
    textDecorationColor: `var(--app-text-error, ${APP_TEXT_ERROR_LIGHT})`,
    fontSize: 12,
  },
};

export const globalTextStylesDisabled: ITextStyles = {
  root: {
    color: `var(--app-text-muted, ${APP_TEXT_MUTED_LIGHT})`,
    textDecorationColor: `var(--app-text-muted, ${APP_TEXT_MUTED_LIGHT})`,
  },
};

export const globalTextStylesCategory: ITextStyles = {
  root: {
    color: `var(--app-text-muted, ${APP_TEXT_MUTED_LIGHT})`,
    textDecorationColor: `var(--app-text-muted, ${APP_TEXT_MUTED_LIGHT})`,
  },
};

/**
 * Theme-aware CSS values for the Project rail/shell (border, background, colour), each with a
 * palette fallback for browsers without CSS variable support.
 */
export const globalCssVarDivider: string = `var(--app-divider, ${APP_DIVIDER_LIGHT})`;
export const globalCssVarLineStrong: string = `var(--app-line-strong, ${APP_LINE_STRONG_LIGHT})`;
export const globalCssVarRowHover: string = `var(--app-row-hover, ${APP_ROW_HOVER_LIGHT})`;
export const globalCssVarSuccess: string = `var(--app-success, ${APP_SUCCESS_LIGHT})`;
export const globalCssVarSurfaceStandout: string = `var(--app-surface-standout, ${APP_SURFACE_STANDOUT_LIGHT})`;
export const globalCssVarSurfaceOverlay: string = `var(--app-surface-overlay, ${APP_SURFACE_OVERLAY_LIGHT})`;

/** The same muted/error text tokens as `globalTextStylesDisabled`/`globalTextStylesError`, as raw CSS values. */
export const globalCssVarTextMuted: string = `var(--app-text-muted, ${APP_TEXT_MUTED_LIGHT})`;
export const globalCssVarTextError: string = `var(--app-text-error, ${APP_TEXT_ERROR_LIGHT})`;

// Green circular checkmarks for `<ul class="ip-outcome-list">`: authored outcome sections in the guide,
// read-only rich text and the editor. The list keeps plain ul/li semantics; the marks are CSS only.
const OUTCOME_LIST_CHECK_MASK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20zm-1.5 13.6 6.4-6.4-1.4-1.4-5 5-2.4-2.4-1.4 1.4z'/%3E%3C/svg%3E") center / contain no-repeat`;

export const globalOutcomeListStyles: IRawStyle = {
  '& ul.ip-outcome-list': { listStyle: 'none', paddingLeft: 0 },
  '& ul.ip-outcome-list > li': { position: 'relative', paddingLeft: 24, marginBottom: 5 },
  '& ul.ip-outcome-list > li::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '.2em',
    width: 14,
    height: 14,
    backgroundColor: globalCssVarSuccess,
    WebkitMask: OUTCOME_LIST_CHECK_MASK,
    mask: OUTCOME_LIST_CHECK_MASK,
  },
};

/** The same rules as a stylesheet for the TinyMCE iframe, which cannot see the host's --app-success variable. */
export const globalOutcomeListCss = (useDarkMode: boolean): string =>
  'ul.ip-outcome-list { list-style: none; padding-left: 0; } ' +
  'ul.ip-outcome-list > li { position: relative; padding-left: 24px; margin-bottom: 5px; } ' +
  `ul.ip-outcome-list > li::before { content: ""; position: absolute; left: 0; top: .2em; width: 14px; height: 14px; background-color: ${
    useDarkMode ? APP_SUCCESS_DARK : APP_SUCCESS_LIGHT
  }; -webkit-mask: ${OUTCOME_LIST_CHECK_MASK}; mask: ${OUTCOME_LIST_CHECK_MASK}; }`;

/** Prose rules for authored rich text (guide, reference card): tight paragraph rhythm, 20px list indent, checkmark lists. */
export const globalRichTextProseStyles: IRawStyle = {
  '& > :first-child': { marginTop: 0 },
  '& > :last-child': { marginBottom: 0 },
  '& p': { marginTop: 0, marginBottom: globalStackTokensGapSmall.childrenGap },
  '& p:has(> strong:only-child)': {
    marginTop: globalStackTokensGapMedium.childrenGap,
    marginBottom: globalStackTokensGapSmall.childrenGap,
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
    marginTop: globalStackTokensGapSmall.childrenGap,
    marginBottom: globalStackTokensGapSmall.childrenGap,
  },
  '& ul, & ol': { margin: 0, paddingLeft: globalStackTokensGapMedium.childrenGap as number },
  ...globalOutcomeListStyles,
};

/**
 * Sets the data-theme attribute and theme-conditional CSS variables on :root so
 * the static style exports above (~106 call sites combined) automatically pick
 * the right colour based on dark-mode state. Called from AppContextProvider on
 * initial load (from localStorage) and on user dark-mode toggle.
 *
 * data-theme is set in addition to the CSS variables: the variables do the
 * actual work today, but the attribute makes the document state inspectable
 * (DevTools) and gives us a hook for any future CSS rule that wants to react
 * to dark mode without inventing yet another variable.
 */
export function applyThemeCssVars(useDarkMode: boolean): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', useDarkMode ? 'dark' : 'light');
  root.style.setProperty('--app-text-error', useDarkMode ? APP_TEXT_ERROR_DARK : APP_TEXT_ERROR_LIGHT);
  root.style.setProperty('--app-text-muted', useDarkMode ? APP_TEXT_MUTED_DARK : APP_TEXT_MUTED_LIGHT);
  root.style.setProperty('--app-divider', useDarkMode ? APP_DIVIDER_DARK : APP_DIVIDER_LIGHT);
  root.style.setProperty('--app-line-strong', useDarkMode ? APP_LINE_STRONG_DARK : APP_LINE_STRONG_LIGHT);
  root.style.setProperty('--app-row-hover', useDarkMode ? APP_ROW_HOVER_DARK : APP_ROW_HOVER_LIGHT);
  root.style.setProperty('--app-success', useDarkMode ? APP_SUCCESS_DARK : APP_SUCCESS_LIGHT);
  root.style.setProperty(
    '--app-surface-standout',
    useDarkMode ? APP_SURFACE_STANDOUT_DARK : APP_SURFACE_STANDOUT_LIGHT,
  );
  root.style.setProperty('--app-surface-overlay', useDarkMode ? APP_SURFACE_OVERLAY_DARK : APP_SURFACE_OVERLAY_LIGHT);
}

//
// Text field styles
//
export const globalTextBig: Partial<ITextStyles> = {
  root: {
    fontWeight: globalFontBoldWeight,
    fontSize: 20,
    paddingLeft: 0,
    lineHeight: '150%',
    maxHeight: 100,
    overflowY: 'scroll',
  },
};

export const globalTextFieldBig: Partial<ITextFieldStyles> = {
  field: {
    fontWeight: globalFontBoldWeight,
    fontSize: 20,
    paddingLeft: 0,
    lineHeight: '150%',
    maxHeight: 100,
    overflowY: 'scroll',
  },
  fieldGroup: { fontWeight: globalFontBoldWeight, fontSize: 20 },
};

export const getGlobalTextFieldDisabled = (
  appContext: IAppContext,
  theme: ITheme,
  width?: number,
): Partial<ITextFieldStyles> => {
  return {
    root: {
      width: width,
    },
    fieldGroup: {
      background: appContext.useDarkMode ? theme.palette.themeDarkAlt : theme.palette.themeLighterAlt,
    },
  };
};

//
// DetailsList styles
//
export const columnCenterStyle: Partial<IDetailsColumnStyles> = {
  root: {
    textAlign: 'center',
  },
};

//
// IconButtons
//
export const acceptIcon: IIconProps = { iconName: 'Accept' };
export const announcementsIcon: IIconProps = { iconName: 'Megaphone' };
export const approvalIcon: IIconProps = { iconName: 'DocumentApproval' };
export const assetIcon: IIconProps = { iconName: 'CompanyDirectory' };
export const backIcon: IIconProps = { iconName: 'Back' };
export const cancelIcon: IIconProps = { iconName: 'Cancel' };
export const certificateIcon: IIconProps = { iconName: 'Certificate' };
export const checkIcon: IIconProps = { iconName: 'CheckMark' };
export const checkSmallIcon: IIconProps = { iconName: 'StatusCircleCheckmark' };
export const chatBotIcon: IIconProps = { iconName: 'ChatBot' };
export const collapseIcon: IIconProps = { iconName: 'BoxSubtractSolid' };
export const expandIcon: IIconProps = { iconName: 'BoxAdditionSolid' };
export const collapseCircleIcon: IIconProps = { iconName: 'SkypeCircleMinus' };
export const expandCircleIcon: IIconProps = { iconName: 'CircleAdditionSolid' };
export const commentIcon: IIconProps = { iconName: 'Comment' };
export const configIcon: IIconProps = { iconName: 'ConfigurationSolid' };
export const controlIcon: IIconProps = { iconName: 'Bullseye' };
export const copyIcon: IIconProps = { iconName: 'Copy' };
export const idIcon: IIconProps = { iconName: 'NumberSymbol' };
export const deleteIcon: IIconProps = { iconName: 'Delete' };
export const discardIcon: IIconProps = { iconName: 'Undo' };
export const documentIcon: IIconProps = { iconName: 'TextDocument' };
export const downArrowIcon: IIconProps = { iconName: 'ChevronDown' };
export const editIcon: IIconProps = { iconName: 'Edit' };
export const errorIcon: IIconProps = { iconName: 'StatusErrorFull' };
export const errorSmallIcon: IIconProps = { iconName: 'StatusCircleErrorX' };
export const eventIcon: IIconProps = { iconName: 'Event' };
export const extLinkIcon: IIconProps = { iconName: 'OpenInNewWindow' };
export const feedbackIcon: IIconProps = { iconName: 'Feedback' };
export const filterICon: IIconProps = { iconName: 'Filter' };
export const folderIcon: IIconProps = { iconName: 'FabricFolder' };
export const formIcon: IIconProps = { iconName: 'EntitlementPolicy' };
export const groupIcon: IIconProps = { iconName: 'Group' };
export const guestIcon: IIconProps = { iconName: 'UserOptional' };
export const helpIcon: IIconProps = { iconName: 'Help' };
export const homeIcon: IIconProps = { iconName: 'Home' };
export const incidentIcon: IIconProps = { iconName: 'IncidentTriangle' };
export const infoIcon: IIconProps = { iconName: 'Info' };
export const kpiIcon: IIconProps = { iconName: 'Chart' };
export const leftArrowIcon: IIconProps = { iconName: 'ChevronLeft' };
export const linkIcon: IIconProps = { iconName: 'Link' };
export const listTypeCustomListIcon: IIconProps = { iconName: 'customList' };
export const listTypeDocLibraryIcon: IIconProps = { iconName: 'DocLibrary' };
export const listTypeSitePageLibraryIcon: IIconProps = { iconName: 'Page' };
export const listTypeWebURLIcon: IIconProps = { iconName: 'Globe' };
export const libraryIcon: IIconProps = { iconName: 'DocumentSet' };
export const lightbulbIcon: IIconProps = { iconName: 'Lightbulb' };
export const moreIcon: IIconProps = { iconName: 'More' };
export const newIcon: IIconProps = { iconName: 'Add' };
export const objectiveIcon: IIconProps = { iconName: 'VersionControlPush' };
export const openExtIcon: IIconProps = { iconName: 'OpenInNewTab' };
export const orgIcon: IIconProps = { iconName: 'Org' };
export const outlookIcon: IIconProps = { iconName: 'OutlookLogo' };
export const pendingIcon: IIconProps = { iconName: 'CircleRing' };
export const pinIcon: IIconProps = { iconName: 'Pin' };
export const processIcon: IIconProps = { iconName: 'SplitObject' };
export const refreshIcon: IIconProps = { iconName: 'Refresh' };
export const rejectIcon: IIconProps = { iconName: 'CalculatorMultiply' };
export const removeIcon: IIconProps = { iconName: 'Remove' };
export const renameIcon: IIconProps = { iconName: 'Rename' };
export const replaceIcon: IIconProps = { iconName: 'SingleColumnEdit' };
export const resourcePanelIcon: IIconProps = { iconName: 'Relationship' };
export const rightArrowIcon: IIconProps = { iconName: 'ChevronRight' };
export const riskIcon: IIconProps = { iconName: 'Warning12' };
export const saveIcon: IIconProps = { iconName: 'Save' };
export const selectionIcon: IIconProps = { iconName: 'MultiSelect' };
export const setOwnerIcon: IIconProps = { iconName: 'FabricUserFolder' };
export const settingsIcon: IIconProps = { iconName: 'Settings' };
export const shareIcon: IIconProps = { iconName: 'Share' };
export const sharepointIcon: IIconProps = { iconName: 'SharepointLogo' };
export const standardsIcon: IIconProps = { iconName: 'MapLayers' };
export const permissionsIcon: IIconProps = { iconName: 'Permissions' };
export const tagIcon: IIconProps = { iconName: 'Tag' };
export const taskIcon: IIconProps = { iconName: 'TaskLogo' };
export const taskStartIcon: IIconProps = { iconName: 'ReminderTime' };
export const teamsIcon: IIconProps = { iconName: 'TeamsLogo' };
export const templateIcon: IIconProps = { iconName: 'scopeTemplate' };
export const themeIcon: IIconProps = { iconName: 'ButtonControl' };
export const unPinIcon: IIconProps = { iconName: 'Unpin' };
export const unknownIcon: IIconProps = { iconName: 'UnknownSolid' };
export const upArrowIcon: IIconProps = { iconName: 'ChevronUp' };
export const uploadIcon: IIconProps = { iconName: 'Upload' };
export const userIcon: IIconProps = { iconName: 'UserFollowed' };
export const validIcon: IIconProps = { iconName: 'SkypeCircleCheck' };
export const validateAllIcon: IIconProps = { iconName: 'ReleaseGateCheck' };
export const validateIcon: IIconProps = { iconName: 'ReleaseGate' };
export const videoIcon: IIconProps = { iconName: 'MSNVideosSolid' };
export const viewIcon: IIconProps = { iconName: 'View' };
export const webIcon: IIconProps = { iconName: 'World' };
export const workflowIcon: IIconProps = { iconName: 'WorkFlow' };
export const applyIcon: IIconProps = { iconName: 'DependencyAdd' };
export const switchIcon: IIconProps = { iconName: 'Switch' };
export const plannerIcon: IIconProps = { iconName: 'PlannerLogo' };
export const recentIcon: IIconProps = { iconName: 'Recent' };
export const startIcon: IIconProps = { iconName: 'Play' };
// An AI process offered from a scene menu. Deliberately not the Play icon: the menu opens the panel on
// the process, the panel's own button starts it.
export const aiProcessIcon: IIconProps = { iconName: 'Processing' };
// An AI action that changes the scene the user is looking at, right now.
export const aiInlineIcon: IIconProps = { iconName: 'LightningBolt' };
// An unattended AI run the scene starts itself.
export const aiBackgroundIcon: IIconProps = { iconName: 'Robot' };
export const inProgressIcon: IIconProps = { iconName: 'ProgressLoopInner' };
export const completedIcon: IIconProps = { iconName: 'Completed' };
export const closeIcon: IIconProps = { iconName: 'ChromeClose' };
export const stopIcon: IIconProps = { iconName: 'Stop' };
export const restartIcon: IIconProps = { iconName: 'PlaybackRate1x' };
export const processingActivityIcon: IIconProps = { iconName: 'Shield' };
export const publishIcon: IIconProps = { iconName: 'PublishContent' };
export const unpublishIcon: IIconProps = { iconName: 'UnpublishContent' };
export const circleIcon: IIconProps = { iconName: 'CircleFill' };
export const upIcon: IIconProps = { iconName: 'Up' };
export const archivedIcon: IIconProps = { iconName: 'Archive' };
export const removeFilterIcon: IIconProps = { iconName: 'RemoveFilter' };
export const complianceIcon: IIconProps = { iconName: 'ComplianceAudit' };
export const approvalPAIcon: IIconProps = { iconName: 'EntitlementRedemption' };
export const runIcon: IIconProps = { iconName: 'Rerun' };
export const disableMonitoringIcon: IIconProps = { iconName: 'RemoveEvent' };
export const enableMonitoringIcon: IIconProps = { iconName: 'Timeline' };
export const storeIcon: IIconProps = { iconName: 'OfficeStoreLogo' };
export const suppliersAndAssetsIcon: IIconProps = { iconName: 'FormLibrary' };

export const imagePropsCopilotIcon: IImageProps = {
  src: `${Config.getImageURL()}/ai-icon.svg`,
  imageFit: IPImageFit.centerContain,
  width: 16,
  height: 16,
  shouldFadeIn: false,
};

export const copilotIconProps: IIconProps = { imageProps: imagePropsCopilotIcon };

export const imagePropsCopilotImageButton: IImageProps = {
  src: `${Config.getImageURL()}/ai-icon.svg`,
  imageFit: IPImageFit.centerContain,
  width: 30,
  height: 30,
};

export const imagePropsCopilotImageLarge: IImageProps = {
  src: `${Config.getImageURL()}/copilot-image-3.png`,
  imageFit: IPImageFit.centerContain,
  width: 220,
  height: 280,
};

export const imagePropsPageLocked: IImageProps = {
  src: `${Config.getImageURL()}/page-locked.png`,
  imageFit: IPImageFit.centerContain,
  width: 128,
  height: 128,
};

//A tooltip host wraps its children in a div that shrinks to the content by default, which would pull a
//full-width field out of its column. Use this when the tooltip sits on a form field rather than a label.
export const globalTooltipHostBlockStyles = { root: { display: 'block' } };

/**
 * Off-screen but read by screen readers. Must stay a 1px clipped box: screen readers skip
 * `display: none`, `visibility: hidden` and `width: 0`.
 */
export const globalVisuallyHiddenStyles = (): IRawStyle => {
  return {
    position: 'absolute',
    width: '1px',
    height: '1px',
    margin: '-1px',
    padding: '0px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    border: '0px',
    clipPath: 'inset(50%)',
  };
};

export const getFontIconColorPrimary = (useDarkMode: boolean, theme: ITheme): string => {
  return useDarkMode ? theme.palette.themeTertiary : theme.palette.themePrimary;
};
